import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { SearchContainerProps } from "./SearchContainer";
import { Form, InputRef } from "antd";
import { useVisibility } from "hooks/useVisibility";
import { useDispatch, useSelector } from "react-redux";
import {
  getRecentSearches,
  getSavedViews,
  getSelectedQueryModal,
} from "redux/SingleDataset/selectors";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import { SavedView } from "../../../../../../types";
import useVqlParams from "hooks/useVqlParams";
import { createAndUpdateSearchTextFilter } from "helpers/utility/vqlHelpers";
import {
  AUTOMATIC_FLAT_RESPONSE_PARAMETER,
  PAGE_PARAMETER,
  SEARCH_TEXT_PARAMETER,
  SEARCH_TEXT_THRESHOLD_PARAMETER,
} from "helpers/constants/miscellaneous";
import { useHotkeys } from "react-hotkeys-hook";

import { useOnClickOutside } from "../../../../../../hooks/useOnClickOutside";
import { addToRecentSearches } from "../../../../../../redux/SingleDataset/actions";
import useNavigateToView from "../../../../../../hooks/useNavigateToView";
import { useDataPageRightPanelContext } from "../../../../../providers/DataPageRightPanelProvider";
import { useFeatureFlagService } from "../../../../../../hooks/useFeatureFlagService";
import {
  useSearchOptions,
  useSearchByRelevanceEnabled,
  useRelevanceScores,
} from "../../../../../../hooks/useFeatureFlagHooks";
import {
  VQL_UPGRADE,
  VL_CHAT,
} from "../../../../../../helpers/constants/filters";
import { validateImageUpload } from "../../../../../../helpers/validations";
import { App } from "antd";
import { useCustomMetadata } from "../../../../../../hooks/useCustomMetadata";

export type Mode =
  | "search"
  | "image"
  | "filters"
  | "filter-item"
  | "filter-search";

const RELEVANCE_SCORE_BREAKPOINTS_COUNT = 3;

export const useSearchContainer = ({
  datasetId,
  logSearchAddition,
  logSearchRemoval,
}: SearchContainerProps) => {
  const ref = useRef(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<InputRef>(null);
  const [form] = Form.useForm();
  const { isOpen, open: openMenu, close } = useVisibility();
  const selectedQueryModalData = useSelector(getSelectedQueryModal);
  const isChatEnabled = useFeatureFlagService(VL_CHAT).enabled;
  const dispatch = useDispatch();
  const [mode, setMode] = useState<Mode>("search");
  const [pastedImageFile, setPastedImageFile] = useState<File | null>(null);
  const query = Form.useWatch("query", form);
  const queryParams = useAllQueryParamsFilters();
  const { removeVqlQuery, vqlQueries, addVqlQuery } = useVqlParams();
  const isVqlUpgradeEnabled = useFeatureFlagService(VQL_UPGRADE).enabled;
  const { data: customMetadataData } = useCustomMetadata({
    datasetId,
    enabled: mode === "filters",
  });

  const handleClearSearch = useCallback(() => {
    searchParams.set(PAGE_PARAMETER, "1");
    form.setFieldValue("query", "");
    if (searchParams.has(SEARCH_TEXT_PARAMETER)) {
      searchParams.delete(SEARCH_TEXT_PARAMETER);
      searchParams.delete(SEARCH_TEXT_THRESHOLD_PARAMETER);
      searchParams.delete(AUTOMATIC_FLAT_RESPONSE_PARAMETER);
      if (isVqlUpgradeEnabled) {
        const existingTextQueries = vqlQueries.filter((q) => "text" in q);
        existingTextQueries.forEach((textQuery) => {
          removeVqlQuery(textQuery.id);
        });
      }
      navigate({ search: `?${searchParams.toString()}` });
      logSearchRemoval();
    }
  }, [
    form,
    logSearchRemoval,
    navigate,
    searchParams,
    isVqlUpgradeEnabled,
    vqlQueries,
    removeVqlQuery,
  ]);
  const reset = useCallback(() => {
    close();
    setMode("search");
    setPastedImageFile(null);
  }, [close]);
  const handleClose = useCallback(() => {
    if (mode === "search" && query) {
      handleClearSearch();
    } else {
      reset();
    }
  }, [handleClearSearch, mode, query, reset]);
  useHotkeys("Escape", reset);
  useOnClickOutside(ref, reset);
  useEffect(() => {
    if (mode === "filter-item" && !selectedQueryModalData?.type) {
      reset();
    }
  }, [close, mode, reset, selectedQueryModalData?.type]);
  const searchOptions = useSearchOptions();
  const searchTextParam = queryParams[SEARCH_TEXT_PARAMETER];
  useEffect(() => {
    form.setFieldValue("query", searchTextParam);
  }, [form, searchTextParam]);
  useEffect(() => {
    if (mode === "search") {
      if (!query && isOpen) {
        inputRef.current?.focus();
      }
    }
  }, [close, isOpen, mode, openMenu, query]);
  const isSearchByRelevanceEnabled = useSearchByRelevanceEnabled();
  const relevanceScores = useRelevanceScores();

  const handleVqlSemanticSearch = useCallback(
    (query: string, searchParams: URLSearchParams) => {
      if (searchOptions.semanticSearch) {
        const semanticQuery = createAndUpdateSearchTextFilter(query.trim());

        // Ensure flat mode is included if feature flag is enabled
        const pathSearchParams = new URLSearchParams(searchParams);
        pathSearchParams.set(AUTOMATIC_FLAT_RESPONSE_PARAMETER, "true");
        pathSearchParams.delete(SEARCH_TEXT_PARAMETER);
        pathSearchParams.delete(SEARCH_TEXT_THRESHOLD_PARAMETER);

        const fullPath = `${location.pathname}?${pathSearchParams.toString()}`;
        addVqlQuery(semanticQuery, fullPath);
      } else {
        navigate({ search: `?${searchParams.toString()}` });
      }
    },
    [searchOptions.semanticSearch, navigate, addVqlQuery, location.pathname],
  );

  const handleSearchByText = (query: string) => {
    if (query) {
      searchParams.set(SEARCH_TEXT_PARAMETER, query);
      searchParams.set(AUTOMATIC_FLAT_RESPONSE_PARAMETER, "true");
      if (
        searchOptions.semanticSearch &&
        isSearchByRelevanceEnabled &&
        relevanceScores.length === RELEVANCE_SCORE_BREAKPOINTS_COUNT
      ) {
        searchParams.set(
          SEARCH_TEXT_THRESHOLD_PARAMETER,
          relevanceScores[1].toString(),
        );
      }
      searchParams.set(PAGE_PARAMETER, "1");

      if (isVqlUpgradeEnabled) {
        handleVqlSemanticSearch(query, searchParams);
      } else {
        navigate({ search: `?${searchParams.toString()}` });
      }

      if (datasetId) {
        dispatch(addToRecentSearches(datasetId, query.trim()) as any);
      }
      // Clear input if VQL upgrade is enabled, otherwise keep the query value
      form.setFieldValue("query", isVqlUpgradeEnabled ? "" : query);
      logSearchAddition();
      close();
    } else {
      handleClearSearch();
    }
  };
  const showRelevanceButton =
    !isVqlUpgradeEnabled &&
    searchOptions.semanticSearch &&
    isSearchByRelevanceEnabled &&
    searchTextParam &&
    mode === "search";
  const inputPlaceholder = useMemo(() => {
    if (searchOptions.semanticSearch) return "What are you looking for?";
    if (searchOptions.captionsSearch) return "Search by captions";
    if (searchOptions.labelsSearch) return "Search by labels";
    return "Search by text"; // fallback
  }, [searchOptions]);
  const navigateToView = useNavigateToView();
  const onSavedViewClick = useCallback(
    (view: SavedView) => {
      navigateToView(view);
      handleClose();
    },
    [handleClose, navigateToView],
  );
  const showMode = (mode: Mode) => {
    setMode(mode);
    openMenu();
  };
  const { setMode: setRightPanelMode } = useDataPageRightPanelContext();
  const handleAskChatClick = useCallback(() => {
    setRightPanelMode("chat");
    form.resetFields();
    reset();
  }, [form, reset, setRightPanelMode]);
  const allRecentSearches = useSelector(getRecentSearches);
  const recentSearches = allRecentSearches?.[datasetId] || [];
  const savedViews = useSelector(getSavedViews);
  const handleInputFocus = useCallback(() => {
    const query = inputRef.current!.input!.value;
    if (
      (query && isChatEnabled) ||
      (!query && (savedViews.length || recentSearches.length))
    ) {
      openMenu();
    }
  }, [isChatEnabled, openMenu, recentSearches.length, savedViews.length]);
  useEffect(() => {
    if (
      isOpen &&
      mode === "search" &&
      !query &&
      !savedViews.length &&
      !recentSearches.length
    ) {
      close();
    }
  }, [close, isOpen, mode, query, recentSearches.length, savedViews.length]);
  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      form.setFieldValue("query", e.currentTarget.value);
      if (!value) {
        handleClearSearch();
        handleInputFocus();
      } else {
        if (isChatEnabled) {
          openMenu();
        } else {
          close();
        }
      }
    },
    [close, form, handleClearSearch, isChatEnabled, openMenu, handleInputFocus],
  );
  const handleLeftIconClick = () => {
    if (mode === "search") {
      handleSearchByText(query);
    } else if (mode === "filter-item") {
      setMode("filters");
    }
  };

  const {
    notification: { error },
  } = App.useApp();

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      const file = Array.from(e.clipboardData?.items || [])
        .find((item) => item.type.startsWith("image/"))
        ?.getAsFile();

      if (!file) return;

      e.preventDefault();

      if (validateImageUpload(file)) {
        setPastedImageFile(file);
        setMode("image");
        openMenu();
      } else {
        error({ message: "Invalid file" });
      }
    },
    [error, openMenu],
  );

  useEffect(() => {
    const inputElement = inputRef.current?.input;
    if (!inputElement) return;

    inputElement.addEventListener("paste", handlePaste as any);

    return () => {
      inputElement.removeEventListener("paste", handlePaste as any);
    };
  }, [handlePaste]);

  return {
    mode,
    isOpen,
    inputPlaceholder,
    showRelevanceButton,
    handleSearchByText,
    searchTextParam,
    query,
    form,
    inputRef,
    handleClose,
    ref,
    onSavedViewClick,
    showMode,
    handleAskChatClick,
    recentSearches,
    savedViews,
    handleInputFocus,
    handleInputChange,
    isSearchEnabled: searchOptions.enabled,
    isChatEnabled,
    handleLeftIconClick,
    pastedImageFile,
    customMetadataData,
  };
};
